import { type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
}

export function Table<T extends { id: string }>({ columns, data, onRowClick, empty }: TableProps<T>) {
  if (data.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <div className="-mx-1 overflow-x-auto overscroll-x-contain rounded-lg border border-[var(--color-rule)] bg-[var(--color-surface)] p-2 shadow-[var(--shadow-panel)] sm:mx-0 sm:p-3">
      <table className="w-full text-sm">
        <thead className="bg-[var(--color-paper-2)]">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  'whitespace-nowrap px-3 py-3.5 text-left text-xs font-semibold text-[var(--color-ink)] first:rounded-l-md last:rounded-r-md sm:px-4',
                  col.className,
                )}
                scope="col"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-rule)] bg-[var(--color-surface)]">
          {data.map((row) => (
            <tr
              key={row.id}
              onClick={() => onRowClick?.(row)}
              className={clsx(
                onRowClick && 'cursor-pointer hover:bg-[var(--color-paper-2)] active:bg-[var(--color-accent-soft)]',
              )}
            >
              {columns.map((col) => (
                <td key={col.key} className={clsx('px-3 py-3.5 text-[var(--color-ink)] sm:px-4', col.className)}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
